import { Stack, styled, Typography, type CSSObject } from '@mui/material';
import { NavLink } from '@rumblefish/ui';

export const StyledStack = styled(Stack)(({ theme }) => ({
  zIndex: 1000,
  padding: theme.spacing(0, 0, 16, 0),
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(8),
  [theme.breakpoints.down('lg')]: {
    padding: theme.spacing(0, 0, 9, 0),
  },
  [theme.breakpoints.down('md')]: {
    gap: theme.spacing(4),
  },
}));

export const TextStack = styled(Stack)(({ theme }) => ({
  width: '100%',
  alignItems: 'center',
  textAlign: 'center',
  [theme.breakpoints.down('md')]: {
    gap: theme.spacing(3),
  },
}));

export const Title = styled(Typography)(({ theme }) => ({
  ...(theme.mixins.neutralGradient as CSSObject),
  fontFamily: 'ClashDisplay',
  fontWeight: 650,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  whiteSpace: 'nowrap',
  lineHeight: 1.3,
  fontSize: theme.utils.fluidSize({ minSize: 16, maxSize: 28 }),
  [theme.breakpoints.up('xl')]: {
    fontSize: '28px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
  },
}));

export const CardsGrid = styled(Stack)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: theme.spacing(3),
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  },
}));

export const CtaStack = styled(Stack)(({ theme }) => ({
  width: '100%',
  alignItems: 'center',
  paddingTop: theme.spacing(2),
}));

export const CardWrap = styled(Stack)<{ accentindex: number }>(
  ({ theme, accentindex }) => {
    const accent =
      accentindex % 2 === 0
        ? theme.palette.cyanColors?.PLUS_2
        : theme.palette.raspberryColors?.PLUS_1;

    return {
      position: 'relative',
      display: 'flex',
      borderRadius: '21px',
      ['&::after']: {
        content: '""',
        position: 'absolute',
        inset: '-18px',
        borderRadius: '28px',
        background: `radial-gradient(closest-side, ${accent}30, transparent 72%)`,
        filter: 'blur(18px)',
        opacity: 0,
        transition: 'opacity 0.35s ease',
        pointerEvents: 'none',
        zIndex: -1,
      },
      ['&:hover::after']: {
        opacity: 1,
      },
      ['&:hover .CaseStudyCard-bg']: {
        transform: 'scale(1.08)',
        filter: 'saturate(1) brightness(0.85)',
      },
      ['&:hover .CaseStudyCard-arrow']: {
        transform: 'rotate(0deg)',
        color: theme.palette.invertedContent?.PRIMARY,
      },
      ['&:hover .CaseStudyCard-link']: {
        transform: 'translateY(-4px)',
        borderColor: `${accent}99`,
        boxShadow: `0 0 24px 2px ${accent}24`,
      },
    };
  }
);

export const CardLink = styled(NavLink)(({ theme }) => ({
  position: 'relative',
  flex: 1,
  overflow: 'hidden',
  isolation: 'isolate',
  display: 'block',
  background: theme.palette.invertedColors?.PLUS_8,
  border: `1px solid ${theme.palette.invertedBorder?.LIGHT}`,
  borderRadius: '20px',
  cursor: 'pointer',
  transition:
    'transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease',
}));

export const CardBg = styled('div')<{ imgurl: string }>(({ imgurl }) => ({
  position: 'absolute',
  inset: 0,
  backgroundImage: `url(${imgurl})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  filter: 'saturate(0.9) brightness(0.92)',
  transform: 'scale(1.02)',
  transition: 'transform 0.6s ease, filter 0.4s ease',
  zIndex: 0,
}));

export const CardScrim = styled('div')(() => ({
  position: 'absolute',
  inset: 0,
  background:
    'linear-gradient(168deg, rgba(7,14,21,0.5) 0%, rgba(7,14,21,0.8) 48%, rgba(7,14,21,0.94) 100%)',
  zIndex: 1,
}));

export const CardContent = styled(Stack)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  height: '100%',
  padding: theme.spacing(5, 6),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(4, 5),
  },
}));

export const CardTop = styled(Stack)(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '36px',
}));

export const CardLogo = styled('img')<{ logoislight?: boolean }>(
  ({ theme, logoislight }) => ({
    height: '36px',
    width: '136px',
    objectFit: 'contain',
    objectPosition: 'left center',
    filter:
      theme.palette.type === 'dark' && !logoislight
        ? 'invert(100%) sepia(0%) saturate(0%) hue-rotate(317deg) brightness(102%) contrast(103%)'
        : '',
    opacity: 0.92,
  })
);

export const CardLogoPlaceholder = styled(Typography)(({ theme }) => ({
  color: theme.palette.invertedContent?.PRIMARY,
  display: 'flex',
  alignItems: 'center',
  height: '36px',
}));

export const CardArrow = styled('svg')(({ theme }) => ({
  width: '18px',
  height: '18px',
  flexShrink: 0,
  color: theme.palette.invertedContent?.TETRIARY,
  transform: 'rotate(-45deg)',
  transition: 'transform 0.2s ease, color 0.2s ease',
}));

export const CardHeadline = styled(Typography)(({ theme }) => ({
  ...(theme.mixins.neutralGradient as CSSObject),
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  minHeight: '2.7em',
  textWrap: 'balance',
}));

export const CardLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.invertedContent?.TETRIARY,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
}));

export const CardAccentBar = styled('span')<{ accentindex: number }>(
  ({ theme, accentindex }) => ({
    marginTop: 'auto',
    paddingTop: theme.spacing(1),
    display: 'block',
    width: '32px',
    height: '3px',
    borderRadius: '2px',
    opacity: 0.85,
    background:
      accentindex % 2 === 0
        ? theme.palette.cyanColors?.PLUS_2
        : theme.palette.raspberryColors?.PLUS_1,
  })
);
